import React from 'react';
import {SVG} from '../../constants/assetLists';
import {createPortal} from 'react-dom';

type Props = {
  showModel: boolean;
  onClose: () => void;
  fullWidth?: boolean;
  fullHeight?: boolean;
  width?: number;
  showCloseIcon?: boolean;
  title?: string;
  titleColor?: string;
  titleFontSize?: string;
  titleFontWeight?: string;
  titleIcon?: React.ReactNode;
  fieldTitle?: string;
  titleField?: React.ReactNode;
  badge?: React.ReactNode;
  showHeaderLine?: boolean;
  subTitle?: string;
  draggable?: boolean;
  children: React.ReactNode;
  containerClasses?: string;
  headerClasses?: string;
  titleContainerClasses?: string;
  footer?: React.ReactNode;
  headerTopContent?: React.ReactNode;
  mainContentClasses?: string;
};

const AppDialog = ({
  showModel,
  onClose,
  fullWidth,
  fullHeight = false,
  width,
  title,
  titleColor = '#038d92',
  titleFontSize = '20px',
  titleFontWeight = '500',
  titleIcon,
  fieldTitle,
  titleField,
  showCloseIcon = true,
  badge,
  showHeaderLine = true,
  subTitle,
  draggable = false,
  children,
  footer,
  containerClasses = '',
  headerClasses = '',
  titleContainerClasses = '',
  headerTopContent,
  mainContentClasses = '',
}: Props) => {
  // offset from the centered position
  const [offset, setOffset] = React.useState({x: 0, y: 0});

  // keep drag state outside of React state to avoid extra re-renders
  const dragStateRef = React.useRef<{
    startX: number;
    startY: number;
    originX: number;
    originY: number;
  } | null>(null);

  const handleMouseDownOnHandle: React.MouseEventHandler<
    HTMLDivElement
  > = e => {
    if (!draggable) return;
    e.preventDefault();

    dragStateRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      originX: offset.x,
      originY: offset.y,
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (!dragStateRef.current) return;
    const {startX, startY, originX, originY} = dragStateRef.current;
    const dx = e.clientX - startX;
    const dy = e.clientY - startY;
    setOffset({x: originX + dx, y: originY + dy});
  };

  const handleMouseUp = () => {
    dragStateRef.current = null;
    window.removeEventListener('mousemove', handleMouseMove);
    window.removeEventListener('mouseup', handleMouseUp);
  };

  // Clean up if dialog closes while dragging
  React.useEffect(() => {
    if (!showModel) {
      dragStateRef.current = null;
      setOffset({x: 0, y: 0});
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showModel]);

  if (!showModel) return null;

  const modal = (
    <div
      className="inset-0 z-50 fixed overflow-hidden"
      style={{backgroundColor: 'rgba(0, 0, 0, 0.2)'}}>
      <div className="w-full h-full flex justify-center items-center">
        <div
          className={`bg-white rounded-[30px] shadow-md border border-black flex flex-col gap-2 pt-[28px]  overflow-hidden ${containerClasses}`}
          style={{
            paddingBottom: footer ? 0 : 28,
            width: fullWidth ? '80%' : width ? width : '500px',
            maxWidth: '95vw',
            ...(fullHeight && {height: '95vh'}),
            maxHeight: '95vh',
            transform: `translate(${offset.x}px, ${offset.y}px)`,
            transition: dragStateRef.current
              ? 'none'
              : 'transform 0.1s ease-out',
          }}>
          {/* Header */}
          <div className={`flex flex-col gap-4 px-[32px] ${headerClasses}`}>
            {(titleIcon || showCloseIcon || title) && (
              <div className="flex justify-between items-center">
                <div
                  className={`flex items-center gap-4 ${titleContainerClasses}`}>
                  {titleIcon && (
                    <div
                      className={draggable ? 'cursor-move' : ''}
                      onMouseDown={handleMouseDownOnHandle}>
                      {titleIcon}
                    </div>
                  )}

                  {title && (
                    <p
                      className="m-0"
                      style={{
                        color: titleColor,
                        fontSize: titleFontSize,
                        fontWeight: titleFontWeight,
                      }}>
                      {title}
                    </p>
                  )}
                </div>

                {showCloseIcon && (
                  <button
                    className="w-[14px] h-[14px] hover:opacity-40 transition-opacity duration-200"
                    onClick={() => onClose()}>
                    <img
                      src={SVG.closeDarkIcon}
                      alt="close"
                      className="w-full h-full object-contain"
                    />
                  </button>
                )}
              </div>
            )}

            {headerTopContent && (
              <div className="w-full">{headerTopContent}</div>
            )}

            {(titleField || fieldTitle) && (
              <div
                className={`flex flex-col gap-1 ${footer ? 'px-0' : 'px-3'}`}>
                <div className="flex justify-content-between items-center gap-2">
                  {fieldTitle && (
                    <p className="m-0 flex-1 text-[32px] text-black">
                      {fieldTitle}
                    </p>
                  )}

                  {titleField && <div className="flex-1">{titleField}</div>}

                  {badge && <div>{badge}</div>}
                </div>
                {showHeaderLine && (
                  <div className="w-full h-[2px] bg-linkBlue rounded-full" />
                )}
                {subTitle && (
                  <p className="m-0 text-sm text-grayText">{subTitle}</p>
                )}
              </div>
            )}
          </div>

          <div
            className={`flex-1 min-h-0 px-3 ${footer ? 'overflow-y-auto' : ''} ${mainContentClasses}`}>
            {children}
          </div>
          {footer && (
            <div className="sticky bottom-0 bg-white  px-6 py-4">{footer}</div>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
};

export default AppDialog;
